import { z } from 'zod';

/**
 * 트랜잭션 하나를 이루는 span 한 건 (7회차 결정 1).
 *
 * 6회차까지는 서버 normalizer가 자식 span을 `sqlCount`·`sqlTimeMs`·`extTimeMs`로 접고 버렸다.
 * 요청 상세 화면은 "요청 안의 SQL을 순서대로, 문장까지" 보여야 하므로 개별 span을 남긴다.
 *
 * 수집 범위는 규칙 4를 따른다. 2026-09-07 결정 7로 SQL 문장의 리터럴과 바인드 값(`params`)을
 * 있는 그대로 남긴다(사내 관제 도구). 요청·응답 본문과 헤더, URL 서명 토큰은 여전히 제외다.
 * 오류는 **코드만** 남긴다. pg 오류 메시지 본문에는 값이 섞이므로 저장하지 않는다.
 */

/**
 * span의 역할. 화면의 구간 막대 색과 목록 분류가 이 값 하나만 본다.
 * - `root`     트랜잭션 진입점 span
 * - `sql`      pg 쿼리
 * - `ext`      외부 HTTP 호출(undici)
 * - `internal` 그 밖(express·nest 등)
 */
export const SpanKindLabel = z.enum(['root', 'sql', 'ext', 'internal']);
export type SpanKindLabel = z.infer<typeof SpanKindLabel>;

/** span 종료 상태. 트랜잭션의 `TxStatus`와 달리 두 가지뿐이다 */
export const SpanStatus = z.enum(['ok', 'error']);
export type SpanStatus = z.infer<typeof SpanStatus>;

export const SpanRecord = z.object({
  /** W3C trace-id. 32자 소문자 hex */
  traceId: z.string().regex(/^[0-9a-f]{32}$/),
  /**
   * OTel service.name (26회차, 23-C 결정 항목 1).
   *
   * 25회차까지 span 행에는 서비스가 없었다. 저장 폴더는 요약 쪽 이름으로 골랐고, 그래서
   * 느린 SQL 통계(`GET /api/stats/sql`)는 언제나 **전 서비스 합산**이었다. 서비스가 둘이면
   * 어느 앱의 문장인지 말할 수 없다. 26회차 전에 저장한 파일에는 이 필드가 없어 NULL로 온다
   */
  service: z.string().optional(),
  /** OTel `service.instance.id`. 어느 프로세스의 span인지. 없을 수 있다 */
  instance: z.string().optional(),
  /** 이 span이 속한 트랜잭션 id(= root span id). 16자 소문자 hex */
  txId: z.string().regex(/^[0-9a-f]{16}$/),
  spanId: z.string().regex(/^[0-9a-f]{16}$/),
  /** root는 원격 부모가 있을 수 있어 값이 실린다 */
  parentSpanId: z
    .string()
    .regex(/^[0-9a-f]{16}$/)
    .optional(),
  kind: SpanKindLabel,
  /** span 이름. 예 `pg.query:SELECT oneworks`, `GET /api/v2/proposals` */
  name: z.string().min(1),
  /** 시작 시각. epoch ms */
  startedAt: z.number().int().nonnegative(),
  durationMs: z.number().nonnegative(),
  status: SpanStatus,
  /** 예외 클래스명. 메시지는 넣지 않는다 */
  errorType: z.string().optional(),
  /**
   * DB 오류 코드(SQLSTATE 5자리. 예 `22003`, `23505`, `42P01`, `57014`).
   * pg 계측이 예외 메시지에 남긴 `code: XXXXX`에서 뽑는다.
   */
  errorCode: z.string().optional(),
  /** SQL 문장. `kind: 'sql'`에만 실린다 */
  query: z.string().optional(),
  /**
   * SQL 바인드 값(`$1`, `$2`… 순서). 문자열로 바꿔 담는다. `kind: 'sql'`에만 실린다.
   * 에이전트가 `apm.sql_params`(JSON 배열 문자열)로 보낸 것을 서버가 풀어 놓은 것이다
   * (2026-09-07 결정 7). 화면 표시는 다음 회차.
   */
  params: z.array(z.string()).optional(),
  /**
   * SQL 문장이 에이전트의 전송 상한에 잘렸다 (22-B회차, UI 피드백 3). 에이전트가
   * `apm.sql_truncated`를 붙인 span에만 참이고, 그 밖에는 없다. 화면이 "뒤가 잘렸다"를 적는다
   */
  queryTruncated: z.boolean().optional(),
  /** 외부 호출 대상 `host + path`. 쿼리스트링은 뺀다. `kind: 'ext'`에만 실린다 */
  target: z.string().optional(),
});
export type SpanRecord = z.infer<typeof SpanRecord>;
